import axios from 'axios';
import { getApiUrl } from '../common/EnvConfig';

export interface WorfklowExecutionOptions {
    executionPlan: string;
    scheduledTime?: string;
    delayed?: boolean;
    comment?: string;
    selectedActions?: string[];
    forcedUpdateActions?: string[];
    continueActions?: string[];
}

class WindsorApi {
    private endpoint: string;

    constructor() {
        this.endpoint = getApiUrl();
    }

    getCurrentUser() {
        return axios.get(`${this.endpoint}/user`).then(res => res.data);
    }

    getCspGroups() {
        return axios.get(`${this.endpoint}/groups`).then(res => res.data);
    }

    getCspGroup(groupId: string) {
        return axios.get(`${this.endpoint}/groups/${groupId}`).then(res => res.data);
    }

    getApprovalRequests() {
        return axios.get(`${this.endpoint}/approvals`).then(res => res.data);
    }

    getExecution(familyId: string, executionId: string) {
        return axios.get(`${this.endpoint}/families/${familyId}/executions/${executionId}`).then(res => res.data);
    }

    getApprovalRequest(approvalId: string) {
        return axios.get(`${this.endpoint}/approvals/${approvalId}`).then(res => res.data);
    }

    getEnvironments() {
        return axios.get(`${this.endpoint}/environments`).then(res => res.data);
    }

    getFaroDeployment(deploymentName: string, accountId: string, region: string, global: boolean) {
        return axios.get(`${this.endpoint}/faroproxy/v1/deployments/${deploymentName}`, {
            params: {
                AccountID: accountId,
                region: region,
                global
            }
        }).then(res => res.data).catch(error => Promise.reject(error.response));
    }

    async getFaroDeployments(deploymentName: string, accountId: string, region: string, global: boolean, axiosOptions?: any) {
        const results = [];
        let lastEvaluatedKey = null;

        do {
            const response = await axios.get(`${this.endpoint}/faroproxy/v2/deployments`, {
                params: {
                    deployment: deploymentName,
                    AccountID: accountId,
                    region: region,
                    global: global ? true : undefined,
                    regional: global ? undefined : true,
                    exclusivestartkey: lastEvaluatedKey
                },
                cancelToken: axiosOptions.cancelToken
            }).then(res => res.data);

            if (response.Deployments) {
                results.push(...response.Deployments);
            }

            lastEvaluatedKey = response.LastEvaluatedKey;
        } while(!!lastEvaluatedKey);

        return results;
    }

    getFaroDeploymentLogs(deploymentName: string, accountId: string, region: string, global: boolean, logId: string) {
        return axios.get(`${this.endpoint}/faro/deployments/${accountId}/${region}/${deploymentName}/logs/${logId}`, {
            params: {
                global
            }
        }).then(res => res.data).catch(error => Promise.reject(error.response));
    }

    getFaroDeck(deckName: string, identifier: string, identifierType: 'tag' | 'version') {
        // Version needs to be encoded twice since it'll be decoded at the proxy
        const versionPath = identifierType === 'version' ? `/versions/${encodeURIComponent(encodeURIComponent(identifier))}` : '';
        return axios.get(`${this.endpoint}/faroproxy/v1/decks/${deckName}${versionPath}`, {
            headers: { Accept: 'application/yaml' },
            params: {
                tag: identifierType === 'tag' && identifier ? identifier : undefined
            }
        }).then(res => res.data).catch(error => Promise.reject(error.response));
    }

    getFaroDeckVersions(deckName: string) {
        return axios.get(`${this.endpoint}/faroproxy/v1/decks/${deckName}/versions`, {
            headers: { Accept: 'application/json' }
        }).then(res => res.data).catch(error => Promise.reject(error.response));
    }

    getFaroDeckPackage(deckName: string, version: string) {
        return axios.request({
            responseType: 'arraybuffer',
            url: `${this.endpoint}/faro/decks/${deckName}/versions/${encodeURIComponent(version)}`,
            method: 'GET'
        }).then(res => res.data).catch(error => Promise.reject(error.response));
    }

    getFaroParameterSet(name: string, version: string, accountId: string, region: string) {
        return axios.get(`${this.endpoint}/faro/parameters/${accountId}/${region}/${name}`, {
            params: {
                version
            }
        }).then(res => res.data).catch(error => Promise.reject(error.response));
    }

    getFaroParameterSetVersions(name: string, accountId: string, region: string) {
        return axios.get(`${this.endpoint}/faroproxy/v1/parameters/${name}/versions`, {
            params: {
                AccountID: accountId,
                region: region
            }
        }).then(res => res.data);
    }

    createParameterSetVersion(parameterSet: any, accountId: string, region: string, assumeRoleArn: string) {
        return axios.post(
            `${this.endpoint}/faro/parameters/${accountId}/${region}`,
            parameterSet,
            {
                headers: {
                    'AssumeRoleARN': assumeRoleArn
                }
            }).then(res => res.data).catch(error => Promise.reject(error.response));
    }

    deleteParameterSet(parameterSetName: string, accountId: string, region: string, assumeRoleArn: string) {
        return axios.delete(
            `${this.endpoint}/faro/parameters/${accountId}/${region}/${parameterSetName}`,
            {
                headers: {
                    'AssumeRoleARN': assumeRoleArn
                }
            }).then(res => res.data).catch(error => Promise.reject(error.response));
    }

    deleteParameterSetVersion(parameterSetName: string, version: string, accountId: string, region: string, assumeRoleArn: string) {
        return axios.delete(
            `${this.endpoint}/faro/parameters/${accountId}/${region}/${parameterSetName}/versions/${version}`,
            {
                headers: {
                    'AssumeRoleARN': assumeRoleArn
                }
            }).then(res => res.data).catch(error => Promise.reject(error.response));
    }

    getFaroParameterSets(accountId: string, region: string, search?: string, axiosOptions?: any) {
        return axios.get(`${this.endpoint}/faroproxy/v1/parameters/search`, {
            params: {
                latest: true,
                AccountID: accountId,
                region: region,
                name: search || undefined
            },
            cancelToken: axiosOptions.cancelToken
        }).then(res => res.data);
    }

    getFaroDecks(search: string, { attributes = undefined } = {}, axiosOptions?: any) {
        return axios.get(`${this.endpoint}/faroproxy/v1/decks`, {
            params: {
                name: search,
                latest: true,
                attributes
            },
            cancelToken: axiosOptions.cancelToken
        }).then(res => res.data);
    }

    getFaroDeckDiff(deckName: string, version1: string, version2: string): Promise<any> {
        return axios.get(`${this.endpoint}/faroproxy/v1/decks/${deckName}/diff`, {
            params: {
                version1,
                version2
                // context: Number.MAX_SAFE_INTEGER
            }
        })
            .then(res => res.data)
            .catch(error => {
                if (error.response && error.response.status === 404) {
                    console.log(`Deck not found: ${deckName}`);
                    // Return empty diff
                    return {
                        "Diff": [],
                        "IncludesDiff": { "NewIncludes": null, "RemovedIncludes": null, "ChangedIncludes": null },
                        "FilesDiff": { "NewFiles": [], "RemovedFiles": [], "ChangedFiles": [] },
                        "LinksDiff": { "NewLinks": [], "RemovedLinks": [], "ChangedLinks": [] },
                        "CloudFormationsDiff": { "New": [], "Removed": [], "Changed": [] },
                        "LocalS3ObjectsDiff": { "New": [], "Removed": [], "Changed": [] },
                        "LocalLambdaLayersDiff": { "New": [], "Removed": [], "Changed": [] },
                        "LocalLambdasDiff": { "New": [], "Removed": [], "Changed": [] },
                        "Version1": version1,
                        "Version2": version2
                    };
                } else {
                    const errorMsg = `Failed to load deck diff: ${deckName}`;
                    console.warn(errorMsg);
                    throw new Error(errorMsg);
                }
            });
    }

    deleteApprovalRequest(ear: any) {
        return axios.delete(`${this.endpoint}/approvals/${ear.GUID}`).catch(error => Promise.reject(error.response));
    }

    approveApprovalRequest(ear: any) {
        return this._handleApprovalRequest(ear, true);
    }

    rejectApprovalRequest(ear: any) {
        return this._handleApprovalRequest(ear, false);
    }

    submitApprovalRequestComment(approvalId: string, comment: string) {
        return axios.put(`${this.endpoint}/approvals/${approvalId}/comment`, {
            'Comment': comment
        }).then(res => res.data);
    }

    getWorkflows(familyId: string) {
        return axios.get(`${this.endpoint}/families/${familyId}/workflows`, {
            params: {
                fields: 'EnableScheduledTests,DailyScheduledExecutionTime,Archived,AccountName,Region,Name,LastModified,Subscribers,GUID',
                includeArchived: true
            }
        }).then(res => res.data);
    }

    updateWorkflow(workflow: any) {
        return axios.put(`${this.endpoint}/families/${workflow.Family}/workflows/${workflow.GUID}`, workflow)
            .then(res => res.data).catch(error => Promise.reject(error.response));
    }

    createWorkflow(workflow: any) {
        return axios.post(`${this.endpoint}/families/${workflow.Family}/workflows`, workflow)
            .then(res => res.data).catch(error => Promise.reject(error.response));
    }

    getWorkflow(familyId: string, workflowId: string, version?: string) {
        return axios.get(`${this.endpoint}/families/${familyId}/workflows/${workflowId}`,
            { params: { version } }).then(res => res.data);
    }

    getWorkflowExecutions(familyId: string, workflowId: string) {
        return axios.get(`${this.endpoint}/families/${familyId}/workflows/${workflowId}/executions`).then(res => res.data);
    }

    getFamilies() {
        return axios.get(`${this.endpoint}/families`)
            .then(res => res.data);
    }

    getFamily(familyId: string) {
        return axios.get(`${this.endpoint}/families/${familyId}`).then(res => res.data);
    }

    updateFamilySubscription(subscribe: boolean, familyId: string) {
        return axios.put(`${this.endpoint}/families/${familyId}/subscription`, {
            'Subscribe': subscribe
        }).then(res => res.data);
    }

    updateFamily(family: any) {
        return axios.put(`${this.endpoint}/families/${family.GUID}`, family).then(res => res.data).catch(error => Promise.reject(error.response));;
    }

    createFamily(family: any) {
        return axios.post(`${this.endpoint}/families`, family).then(res => res.data).catch(error => Promise.reject(error.response));;
    }

    updateWorkflowSubscription(subscribe: boolean, workflowId: string, familyId: string) {
        return axios.put(`${this.endpoint}/families/${familyId}/workflows/${workflowId}/subscription`, {
            'Subscribe': subscribe,
        }).then(res => res.data);
    }

    archiveWorkflow(workflowId: string, familyId: string) {
        return axios.put(`${this.endpoint}/families/${familyId}/workflows/${workflowId}/archive`, {
            archive_value: true
        }).then(res => res.data);
    }

    unarchiveWorkflow(workflowId: string, familyId: string) {
        return axios.put(`${this.endpoint}/families/${familyId}/workflows/${workflowId}/archive`, {
            archive_value: false
        }).then(res => res.data);
    }

    getExecutions(familyId: string) {
        return this._getAllExecutions(familyId, null, []);
    }

    getExecutionActionDeployLogs(familyId: string, executionId: string, actionName: string) {
        return axios.get(`${this.endpoint}/families/${familyId}/executions/${executionId}/${actionName}/deploy_logs`)
            .then(res => res.data).catch(() => 'Deployment logs not found\n');
    }

    getExecutionActionTeardownLogs(familyId: string, executionId: string, actionName: string) {
        return axios.get(`${this.endpoint}/families/${familyId}/executions/${executionId}/${actionName}/teardown_logs`)
            .then(res => res.data).catch(() => 'Teardown logs not found\n');
    }

    runExecution(familyId: string, executionId: string) {
        return axios.put(`${this.endpoint}/families/${familyId}/executions/${executionId}/run`)
            .then(res => res.data);
    }

    cancelExecution(familyId: string, executionId: string) {
        return axios.put(`${this.endpoint}/families/${familyId}/executions/${executionId}/cancel`)
            .then(res => res.data).catch(error => Promise.reject(error.response));
    }

    forceCancelExecution(familyId: string, executionId: string) {
        return axios.put(`${this.endpoint}/families/${familyId}/executions/${executionId}/force-cancel`)
            .then(res => res.data).catch(error => Promise.reject(error.response));
    }

    executeWorkflow(familyId: string, workflowId: string, options: WorfklowExecutionOptions) {
        return axios.post(`${this.endpoint}/families/${familyId}/workflows/${workflowId}/executions`, {
            Delayed: options.delayed,
            ScheduledTime: options.scheduledTime,
            ExecutionComment: options.comment || '',
            ExecutionPlan: options.executionPlan,
            SelectedActions: options.selectedActions,
            ForcedUpdateActions: options.forcedUpdateActions,
            ContinueActions: options.continueActions
        }).then(res => res.data);
    }

    getTests(familyId: string) {
        return axios.get(`${this.endpoint}/families/${familyId}/tests`)
            .then(res => res.data);
    }

    getTestVersions(familyId: string, testName: string) {
        return axios.get(`${this.endpoint}/families/${familyId}/tests/${testName}/versions`)
            .then(res => res.data);
    }

    getTestVersion(familyId: string, testName: string, version: string) {
        return axios.request({
            responseType: 'arraybuffer',
            url: `${this.endpoint}/families/${familyId}/tests/${testName}/versions/${version}`,
            method: 'GET'
        }).then(res => res.data);
    }

    createTest(familyId: string, testName, data) {
        return axios.post(
            `${this.endpoint}/families/${familyId}/tests/${testName}`,
            data,
            {
                headers: {
                    'content-type': 'binary/octet-stream'
                }
            }
        ).then(res => res.data)
            .catch(error => Promise.reject(error.response));
    }

    updateTest(familyId: string, testName, data) {
        return axios.put(
            `${this.endpoint}/families/${familyId}/tests/${testName}`,
            data,
            {
                headers: {
                    'content-type': 'binary/octet-stream'
                }
            }
        ).then(res => res.data)
            .catch(error => Promise.reject(error.response));
    }

    _getAllExecutions(familyId: string, cursor: string, executions: any[]) {
        return axios.get(`${this.endpoint}/families/${familyId}/executions`).then(res => res.data);
    }

    _handleApprovalRequest(ear: any, approved: boolean) {
        return axios.put(`${this.endpoint}/approvals/${ear.GUID}`, {
            'Decision': approved
        }).then(res => res.data);
    }
}

export default WindsorApi;
